import { useMemo, useState } from "react"
import { Database, Plus, RefreshCcw, Trash2 } from "lucide-react"

import type {
  PreviewReportDatasourceResult,
  ReportDatasourceViewOptionDto,
  ReportTemplateDatasourceBindingDto,
  ReportTemplateDatasourceDto,
} from "../../api/reportTemplateService"
import type { ReportDesignerValidationIssue } from "./reportDesignerValidation"
import { ValidationSummaryCard } from "./ValidationSummaryCard"
import { Badge } from "shared/ui/badge"
import { Button } from "shared/ui/button"
import { Card, CardContent, CardHeader, CardTitle } from "shared/ui/card"
import { Input } from "shared/ui/input"
import { Label } from "shared/ui/label"
import { Popover, PopoverContent, PopoverTrigger } from "shared/ui/popover"
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "shared/ui/select"
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "shared/ui/table"

const makeId = () =>
  typeof crypto !== "undefined" && typeof crypto.randomUUID === "function"
    ? crypto.randomUUID()
    : `datasource-${Date.now()}-${Math.random().toString(36).slice(2, 10)}`

const formatCell = (value: unknown) => {
  if (value == null) return "-"
  if (typeof value === "boolean") return value ? "Yes" : "No"
  if (typeof value === "object") return JSON.stringify(value)
  const text = String(value)
  return text.trim() ? text : "-"
}

function ViewPicker({
  value,
  options,
  onChange,
}: {
  value: string
  options: ReportDatasourceViewOptionDto[]
  onChange: (value: string) => void
}) {
  const [open, setOpen] = useState(false)
  const [search, setSearch] = useState("")

  const selected = options.find((item) => item.objectName === value) ?? null
  const filtered = useMemo(() => {
    const keyword = search.trim().toLowerCase()
    if (!keyword) return options
    return options.filter((item) => item.objectName.toLowerCase().includes(keyword))
  }, [options, search])

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button type="button" variant="outline" className="w-full justify-between font-normal">
          <span className="truncate text-left">{selected?.objectName || value || "Select a view"}</span>
          <Database className="ml-2 h-4 w-4 shrink-0 opacity-50" />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-[420px] space-y-2 p-3" align="start">
        <Input
          value={search}
          onChange={(event) => setSearch(event.target.value)}
          placeholder="Search view name"
        />
        <div className="max-h-64 space-y-1 overflow-y-auto pr-1">
          {filtered.map((item) => (
            <Button
              key={item.objectName}
              type="button"
              variant={item.objectName === value ? "default" : "outline"}
              className="h-auto w-full justify-start py-2 text-left"
              onClick={() => {
                onChange(item.objectName)
                setOpen(false)
                setSearch("")
              }}
            >
              {item.objectName}
            </Button>
          ))}
          {filtered.length === 0 ? (
            <div className="py-3 text-center text-sm text-gray-500">No views found</div>
          ) : null}
        </div>
      </PopoverContent>
    </Popover>
  )
}

export interface DataSourcesTabProps {
  datasources: ReportTemplateDatasourceDto[]
  validationIssues: ReportDesignerValidationIssue[]
  viewOptions: ReportDatasourceViewOptionDto[]
  contextKeyOptions: Array<{ key: string; label: string }>
  previewResults: Record<string, PreviewReportDatasourceResult>
  loadingDatasourceId: string | null
  isRefreshingViews: boolean
  onRefreshViews: () => void
  onTestDatasource: (datasourceId: string) => void
  onChange: (datasources: ReportTemplateDatasourceDto[]) => void
}

export function DataSourcesTab({
  datasources,
  validationIssues,
  viewOptions,
  contextKeyOptions,
  previewResults,
  loadingDatasourceId,
  isRefreshingViews,
  onRefreshViews,
  onTestDatasource,
  onChange,
}: DataSourcesTabProps) {
  const updateDatasource = (datasourceId: string, patch: Partial<ReportTemplateDatasourceDto>) => {
    onChange(
      datasources.map((item) => (item.id === datasourceId ? { ...item, ...patch } : item)),
    )
  }

  const updateBindings = (datasourceId: string, bindings: ReportTemplateDatasourceBindingDto[]) => {
    updateDatasource(datasourceId, { bindings })
  }

  const addDatasource = () => {
    onChange([
      ...datasources,
      {
        id: makeId(),
        name: `Datasource ${datasources.length + 1}`,
        sourceType: "view",
        objectName: "",
        cardinality: "record",
        rowLimit: 20,
        bindings: [],
        fields: [],
      },
    ])
  }

  const removeDatasource = (datasourceId: string) => {
    onChange(datasources.filter((item) => item.id !== datasourceId))
  }

  const addBinding = (datasourceId: string) => {
    const datasource = datasources.find((item) => item.id === datasourceId)
    const next = {
      id: makeId(),
      columnName: "",
      contextKey: contextKeyOptions[0]?.key || "",
      operator: "=" as const,
    }
    updateBindings(datasourceId, [...(datasource?.bindings ?? []), next])
  }

  return (
    <div className="space-y-4">
      <ValidationSummaryCard
        title="Datasource Guardrails"
        issues={validationIssues}
        category="datasource"
        emptyMessage="All datasource definitions look consistent."
      />

      <div className="flex items-center justify-between gap-3">
        <div>
          <h3 className="text-sm font-semibold text-slate-900">Report Data Sources</h3>
          <p className="text-xs text-slate-500">
            Select a database view, define whether it returns one record or a table, and map filter columns to runtime context.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Button type="button" variant="outline" size="sm" onClick={onRefreshViews} disabled={isRefreshingViews}>
            <RefreshCcw className={`mr-2 h-4 w-4 ${isRefreshingViews ? "animate-spin" : ""}`} />
            Refresh Views
          </Button>
          <Button type="button" size="sm" onClick={addDatasource}>
            <Plus className="mr-2 h-4 w-4" />
            Add Data Source
          </Button>
        </div>
      </div>

      {datasources.length === 0 ? (
        <Card>
          <CardContent className="py-10 text-center text-sm text-slate-500">
            No data sources yet. Add one to bind record fields or table widgets to a database view.
          </CardContent>
        </Card>
      ) : null}

      {datasources.map((datasource, index) => {
        const preview = previewResults[datasource.id]
        const sampleRows =
          datasource.cardinality === "table"
            ? preview?.rows?.slice(0, 5) ?? []
            : preview?.record
              ? [preview.record]
              : []

        return (
          <Card key={datasource.id}>
            <CardHeader className="pb-3">
              <div className="flex items-start justify-between gap-3">
                <div className="space-y-1">
                  <CardTitle className="text-base">{datasource.name || `Datasource ${index + 1}`}</CardTitle>
                  <div className="flex items-center gap-2">
                    <Badge variant="outline">{datasource.cardinality === "table" ? "Table" : "Record"}</Badge>
                    {preview ? <Badge variant="secondary">{preview.rowCount} row(s)</Badge> : null}
                  </div>
                </div>
                <Button type="button" variant="outline" size="sm" onClick={() => removeDatasource(datasource.id)}>
                  <Trash2 className="mr-2 h-4 w-4" />
                  Remove
                </Button>
              </div>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid gap-3 md:grid-cols-2">
                <div className="space-y-1.5">
                  <Label>Name</Label>
                  <Input
                    value={datasource.name}
                    onChange={(event) => updateDatasource(datasource.id, { name: event.target.value })}
                  />
                </div>
                <div className="space-y-1.5">
                  <Label>Cardinality</Label>
                  <Select
                    value={datasource.cardinality}
                    onValueChange={(value: "record" | "table") => updateDatasource(datasource.id, { cardinality: value })}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="record">Record (single row)</SelectItem>
                      <SelectItem value="table">Table (multiple rows)</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>

              <div className="grid gap-3 md:grid-cols-[minmax(0,1fr)_120px]">
                <div className="space-y-1.5">
                  <Label>View</Label>
                  <ViewPicker
                    value={datasource.objectName}
                    options={viewOptions}
                    onChange={(value) => updateDatasource(datasource.id, { objectName: value })}
                  />
                </div>
                <div className="space-y-1.5">
                  <Label>Row Limit</Label>
                  <Input
                    type="number"
                    min={1}
                    max={200}
                    value={datasource.rowLimit ?? 20}
                    onChange={(event) =>
                      updateDatasource(datasource.id, {
                        rowLimit: Math.max(1, Math.min(200, Number(event.target.value || 20))),
                      })
                    }
                  />
                </div>
              </div>

              <div className="space-y-2 rounded-md border p-3">
                <div className="flex items-center justify-between gap-2">
                  <div>
                    <p className="text-sm font-medium text-slate-700">Bindings</p>
                    <p className="text-xs text-slate-500">
                      Example: map view column `permit_id` to runtime context `permit_id`.
                    </p>
                  </div>
                  <Button type="button" size="sm" variant="outline" onClick={() => addBinding(datasource.id)}>
                    <Plus className="mr-2 h-4 w-4" />
                    Add Binding
                  </Button>
                </div>

                {(datasource.bindings ?? []).length === 0 ? (
                  <p className="text-xs text-slate-500">No bindings. The preview will return top rows from the view.</p>
                ) : (
                  <div className="space-y-2">
                    {(datasource.bindings ?? []).map((binding, bindingIndex) => (
                      <div key={binding.id} className="grid gap-2 md:grid-cols-[1fr_1fr_auto]">
                        <Input
                          value={binding.columnName}
                          onChange={(event) => {
                            const next = [...(datasource.bindings ?? [])]
                            next[bindingIndex] = { ...binding, columnName: event.target.value }
                            updateBindings(datasource.id, next)
                          }}
                          placeholder="View column"
                        />
                        <Select
                          value={binding.contextKey}
                          onValueChange={(value) => {
                            const next = [...(datasource.bindings ?? [])]
                            next[bindingIndex] = { ...binding, contextKey: value }
                            updateBindings(datasource.id, next)
                          }}
                        >
                          <SelectTrigger>
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            {contextKeyOptions.map((item) => (
                              <SelectItem key={`${datasource.id}-${binding.id}-${item.key}`} value={item.key}>
                                {item.label}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                        <Button
                          type="button"
                          variant="outline"
                          size="icon"
                          onClick={() =>
                            updateBindings(
                              datasource.id,
                              (datasource.bindings ?? []).filter((item) => item.id !== binding.id),
                            )
                          }
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              <div className="flex items-center justify-between gap-2">
                <div className="text-xs text-slate-500">
                  Use preview to load schema now so field/table widgets can bind to this source.
                </div>
                <Button type="button" onClick={() => onTestDatasource(datasource.id)} disabled={loadingDatasourceId === datasource.id}>
                  <Database className={`mr-2 h-4 w-4 ${loadingDatasourceId === datasource.id ? "animate-pulse" : ""}`} />
                  {loadingDatasourceId === datasource.id ? "Testing..." : "Test Source"}
                </Button>
              </div>

              {preview ? (
                <div className="space-y-3 rounded-md border bg-slate-50 p-3">
                  <div className="flex flex-wrap items-center gap-2">
                    <Badge variant="outline">{preview.cardinality === "table" ? "Table" : "Record"}</Badge>
                    <span className="text-xs text-slate-500">{preview.columns.length} column(s)</span>
                  </div>

                  <div className="flex flex-wrap gap-2">
                    {preview.columns.map((column) => (
                      <Badge key={`${datasource.id}-${column.fieldKey}`} variant="secondary">
                        {column.label}
                      </Badge>
                    ))}
                  </div>

                  {sampleRows.length > 0 ? (
                    <div className="overflow-x-auto rounded border bg-white">
                      <Table>
                        <TableHeader>
                          <TableRow>
                            {preview.columns.map((column) => (
                              <TableHead key={`${datasource.id}-head-${column.fieldKey}`}>{column.label}</TableHead>
                            ))}
                          </TableRow>
                        </TableHeader>
                        <TableBody>
                          {sampleRows.map((row, rowIndex) => (
                            <TableRow key={`${datasource.id}-row-${rowIndex}`}>
                              {preview.columns.map((column) => (
                                <TableCell key={`${datasource.id}-${rowIndex}-${column.fieldKey}`}>
                                  {formatCell(row?.[column.fieldKey])}
                                </TableCell>
                              ))}
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </div>
                  ) : (
                    <div className="text-sm text-slate-500">No rows returned for current bindings/context.</div>
                  )}
                </div>
              ) : null}
            </CardContent>
          </Card>
        )
      })}
    </div>
  )
}
